// fe/src/modules/billing/UpgradeRequestModal.tsx
// Collects the new user and storage limits and raises a ticket to the commercial manager.
// Limits are the new totals, not amounts added on top.

import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, X } from "lucide-react";
import {
    estimateMonthly,
    formatBytes,
    formatMoney,
    requestPlanUpgrade,
    type PlanResponse,
    type PlanUsage,
} from "./planApi";

interface UpgradeRequestModalProps {
    open: boolean;
    plan: PlanResponse;
    /** Usage as shown on screen (may be a demo scenario). */
    usage: PlanUsage;
    /** Set when the request is raised from a demo scenario; recorded on the ticket. */
    scenarioContext?: string;
    onClose: () => void;
    onRequested: (plan: PlanResponse) => void;
}

type StorageUnit = "MB" | "GB" | "TB";
const UNIT_TO_GB: Record<StorageUnit, number> = { MB: 1 / 1024, GB: 1, TB: 1024 };
const round4 = (n: number) => Math.round(n * 10000) / 10000;

const UpgradeRequestModal: React.FC<UpgradeRequestModalProps> = ({ open, plan, usage, scenarioContext, onClose, onRequested }) => {
    const current = plan.plan;
    // Show storage in the unit the plan was set in.
    const currentUnit: StorageUnit =
        current.storageLimitValue > 0 && current.storageLimitUnit in UNIT_TO_GB ? (current.storageLimitUnit as StorageUnit) : "GB";
    const currentValue = current.storageLimitValue > 0 ? current.storageLimitValue : current.storageLimitGb;

    const [users, setUsers] = useState(String(current.userLimit));
    const [storage, setStorage] = useState(String(currentValue));
    const [unit, setUnit] = useState<StorageUnit>(currentUnit);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ticketRef, setTicketRef] = useState<string | null>(null);
    const [result, setResult] = useState<PlanResponse | null>(null);

    useEffect(() => {
        if (!open) return;
        // Suggest the limit that's actually blocking; the user can change both.
        const usersFull = usage.users.used >= usage.users.limit;
        const storageFull = usage.storage.pct >= 90;
        setUsers(String(usersFull ? current.userLimit + Math.max(1, Math.ceil(current.userLimit / 2)) : current.userLimit));
        setStorage(String(storageFull ? currentValue * 2 : currentValue));
        setUnit(currentUnit);
        setNote("");
        setError(null);
        setTicketRef(null);
        setResult(null);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !submitting) handleClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    if (!open) return null;

    const userLimit = Number(users);
    const storageLimitGb = round4(Number(storage) * UNIT_TO_GB[unit]);
    const sameStorage = Math.abs(storageLimitGb - current.storageLimitGb) < 0.0001;

    let validation: string | null = null;
    if (!Number.isInteger(userLimit) || userLimit < 1) validation = "Enter a whole number of users.";
    else if (!(storageLimitGb > 0)) validation = "Enter a storage limit.";
    else if (userLimit < current.userLimit || (storageLimitGb < current.storageLimitGb && !sameStorage))
        validation = "New limits can't be lower than your current plan.";
    else if (userLimit === current.userLimit && sameStorage)
        validation = "Raise the user limit, the storage limit, or both.";

    const currentPrice = current.price.total;
    const newPrice = validation ? null : estimateMonthly(plan.pricing, userLimit, storageLimitGb);
    const currency = current.price.currency;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validation) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await requestPlanUpgrade({
                userLimit,
                storageLimitGb,
                note: note.trim() || undefined,
                context: scenarioContext,
            });
            setTicketRef(res.ticket.reference);
            setResult(res.plan);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Request failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (result) onRequested(result);
        onClose();
    };

    const input =
        "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="upgrade-title">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 id="upgrade-title" className="text-lg font-semibold text-gray-900">
                            {ticketRef ? "Request sent" : "Request a plan upgrade"}
                        </h2>
                        {!ticketRef && (
                            <p className="mt-1 text-sm text-gray-500">
                                This raises a ticket to your commercial manager. Your plan changes once they approve it.
                            </p>
                        )}
                    </div>
                    <button onClick={handleClose} disabled={submitting} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                {ticketRef ? (
                    <div className="px-6 py-8 text-center">
                        <CheckCircle2 className="mx-auto text-green-600" size={48} />
                        <p className="mt-4 text-gray-900">
                            Ticket {ticketRef} is with your commercial manager.
                        </p>
                        <p className="mt-2 text-sm text-gray-500">
                            You asked for {userLimit} users and {storage} {unit}. Track it under Tickets.
                        </p>
                        <button onClick={handleClose} className="mt-6 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700">
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="px-6 py-5">
                        <div className="grid grid-cols-2 gap-3 rounded-xl bg-gray-50 p-4 text-sm">
                            <div>
                                <p className="text-gray-500">Current plan</p>
                                <p className="mt-0.5 font-medium text-gray-900">
                                    {current.userLimit} users, {current.storageLimitLabel}
                                </p>
                            </div>
                            <div>
                                <p className="text-gray-500">In use</p>
                                <p className="mt-0.5 font-medium text-gray-900">
                                    {usage.users.used} users, {formatBytes(usage.storage.usedBytes)}
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="text-sm text-gray-700">New user limit</span>
                                <input type="number" min={1} step={1} value={users} onChange={(e) => setUsers(e.target.value)} className={`${input} mt-1`} />
                            </label>
                            <div>
                                <label htmlFor="upgrade-storage" className="text-sm text-gray-700">New storage limit</label>
                                <div className="mt-1 flex gap-2">
                                    <input id="upgrade-storage" type="number" min={0} step="any" value={storage} onChange={(e) => setStorage(e.target.value)} className={input} />
                                    <select value={unit} onChange={(e) => setUnit(e.target.value as StorageUnit)} aria-label="Storage unit" className="rounded-lg border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100">
                                        <option>MB</option>
                                        <option>GB</option>
                                        <option>TB</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            These replace your current limits. For example, asking for 10 users and 2 GB gives you 10 users and 2 GB in total.
                        </p>

                        <label className="mt-4 block">
                            <span className="text-sm text-gray-700">Note for your commercial manager (optional)</span>
                            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} className={`${input} mt-1 resize-none`} />
                        </label>

                        <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm">
                            <span className="text-gray-600">Estimated monthly price</span>
                            <span className="text-gray-900">
                                {formatMoney(currentPrice, currency)}
                                {newPrice !== null && (
                                    <>
                                        <span className="mx-2 text-gray-400">to</span>
                                        <span className="font-semibold">{formatMoney(newPrice, currency)}</span>
                                    </>
                                )}
                            </span>
                        </div>

                        {(validation || error) && (
                            <p className={`mt-3 rounded-lg px-3 py-2 text-sm ${error ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                                {error || validation}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={!!validation || submitting}
                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {submitting && <Loader2 size={16} className="animate-spin" />}
                            Send request
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default UpgradeRequestModal;